/**
 * Episode of Care Service
 * 
 * Manages oncology episodes of care - groups of encounters related to
 * a patient's cancer diagnosis and treatment journey.
 */

import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get all episodes of care for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Episodes list with pagination
 */
export const getPatientEpisodes = async (patientId, facilityId, options = {}) => {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams({ facilityId });
    
    if (options.status) params.append('status', options.status);
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());

    const response = await axios.get(
      `${API_BASE}/api/oncology/patients/${patientId}/episodes?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching patient episodes:', error);
    throw error;
  }
};

/**
 * Get all episodes of care for a facility
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Episodes list with pagination
 */
export const getFacilityEpisodes = async (facilityId, options = {}) => {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams({ facilityId });
    
    if (options.status) params.append('status', options.status);
    if (options.currentPhase) params.append('currentPhase', options.currentPhase);
    if (options.search) params.append('search', options.search);
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());

    const response = await axios.get(
      `${API_BASE}/api/oncology/episodes?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching facility episodes:', error);
    throw error;
  }
};

/**
 * Get a single episode of care with full details
 * @param {string} episodeId - Episode ID
 * @returns {Promise<Object>} Episode data with linked records
 */
export const getEpisode = async (episodeId) => {
  try {
    const token = await getAuthToken();

    const response = await axios.get(
      `${API_BASE}/api/oncology/episodes/${episodeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching episode:', error);
    throw error;
  }
};

/**
 * Create a new episode of care
 * @param {Object} episodeData - Episode data
 * @param {string} episodeData.patientId - Patient ID (required)
 * @param {string} episodeData.diagnosisId - Cancer diagnosis ID (required)
 * @param {string} episodeData.facilityId - Facility ID (required)
 * @param {string} [episodeData.episodeName] - Episode name
 * @param {string} [episodeData.initialEncounterId] - Initial encounter ID
 * @param {string} [episodeData.primaryOncologist] - Primary oncologist ID
 * @param {string} [episodeData.currentPhase] - Current phase
 * @returns {Promise<Object>} Created episode
 */
export const createEpisode = async (episodeData) => {
  try {
    if (!episodeData.patientId || !episodeData.diagnosisId || !episodeData.facilityId) {
      throw new Error('Patient ID, diagnosis ID, and facility ID are required');
    }

    const token = await getAuthToken();

    const response = await axios.post(
      `${API_BASE}/api/oncology/episodes`,
      episodeData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating episode:', error);
    throw error;
  }
};

/**
 * Update an episode of care
 * @param {string} episodeId - Episode ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Updated episode
 */
export const updateEpisode = async (episodeId, updateData) => {
  try {
    const token = await getAuthToken();

    const response = await axios.put(
      `${API_BASE}/api/oncology/episodes/${episodeId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error updating episode:', error);
    throw error;
  }
};

/**
 * Link an encounter to an episode
 * @param {string} episodeId - Episode ID
 * @param {string} encounterId - Encounter ID to link
 * @returns {Promise<Object>} Result
 */
export const linkEncounterToEpisode = async (episodeId, encounterId) => {
  try {
    const token = await getAuthToken();

    const response = await axios.post(
      `${API_BASE}/api/oncology/episodes/${episodeId}/link-encounter`,
      { encounterId },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error linking encounter to episode:', error);
    throw error;
  }
};

/**
 * Unlink an encounter from an episode
 * @param {string} episodeId - Episode ID
 * @param {string} encounterId - Encounter ID to unlink
 * @returns {Promise<Object>} Result
 */
export const unlinkEncounterFromEpisode = async (episodeId, encounterId) => {
  try {
    const token = await getAuthToken();

    const response = await axios.post(
      `${API_BASE}/api/oncology/episodes/${episodeId}/unlink-encounter`,
      { encounterId },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error unlinking encounter from episode:', error);
    throw error;
  }
};

/**
 * Link a treatment plan to an episode
 * @param {string} episodeId - Episode ID
 * @param {string} treatmentPlanId - Treatment plan ID to link
 * @returns {Promise<Object>} Result
 */
export const linkTreatmentPlanToEpisode = async (episodeId, treatmentPlanId) => {
  try {
    const token = await getAuthToken();

    const response = await axios.post(
      `${API_BASE}/api/oncology/episodes/${episodeId}/link-treatment-plan`,
      { treatmentPlanId },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error linking treatment plan to episode:', error);
    throw error;
  }
};

/**
 * Get episode timeline (all activities for an episode)
 * @param {string} episodeId - Episode ID
 * @returns {Promise<Object>} Timeline data
 */
export const getEpisodeTimeline = async (episodeId) => {
  try {
    const token = await getAuthToken();

    const response = await axios.get(
      `${API_BASE}/api/oncology/episodes/${episodeId}/timeline`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching episode timeline:', error);
    throw error;
  }
};

/**
 * Create an episode of care automatically when a cancer diagnosis is created
 * This is a convenience function that creates the episode and links it to the diagnosis
 * @param {string} patientId - Patient ID
 * @param {string} diagnosisId - Diagnosis ID
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Created episode
 */
export const createEpisodeFromDiagnosis = async (patientId, diagnosisId, facilityId, options = {}) => {
  try {
    const episodeData = {
      patientId,
      diagnosisId,
      facilityId,
      status: 'Active',
      currentPhase: 'Diagnosis',
      startDate: new Date().toISOString(),
      ...options
    };

    return await createEpisode(episodeData);
  } catch (error) {
    console.error('Error creating episode from diagnosis:', error);
    throw error;
  }
};

/**
 * Get active episode for a patient (most recent active episode)
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object|null>} Active episode or null
 */
export const getActiveEpisode = async (patientId, facilityId) => {
  try {
    const result = await getPatientEpisodes(patientId, facilityId, {
      status: 'Active',
      limit: 1
    });

    return result.data && result.data.length > 0 ? result.data[0] : null;
  } catch (error) {
    console.error('Error fetching active episode:', error);
    return null;
  }
};

// Episode status options
export const EPISODE_STATUS = {
  ACTIVE: 'Active',
  ON_HOLD: 'On Hold',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled'
};

// Episode phase options
export const EPISODE_PHASE = {
  DIAGNOSIS: 'Diagnosis',
  ACTIVE_TREATMENT: 'Active Treatment',
  SURVEILLANCE: 'Surveillance',
  SURVIVORSHIP: 'Survivorship',
  PALLIATIVE: 'Palliative',
  END_OF_LIFE: 'End of Life'
};

// Episode outcome options
export const EPISODE_OUTCOME = {
  COMPLETE_RESPONSE: 'Complete Response',
  PARTIAL_RESPONSE: 'Partial Response',
  STABLE_DISEASE: 'Stable Disease',
  PROGRESSIVE_DISEASE: 'Progressive Disease',
  DECEASED: 'Deceased',
  ONGOING: 'Ongoing'
};

export default {
  getPatientEpisodes,
  getFacilityEpisodes,
  getEpisode,
  createEpisode,
  updateEpisode,
  linkEncounterToEpisode,
  unlinkEncounterFromEpisode,
  linkTreatmentPlanToEpisode,
  getEpisodeTimeline,
  createEpisodeFromDiagnosis,
  getActiveEpisode,
  EPISODE_STATUS,
  EPISODE_PHASE,
  EPISODE_OUTCOME
};
